import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLookup, type LookupItem } from '../lookups';
import { VariableCreateModal } from './VariableCreateModal';

// Sentinel option value for "+ New variable…". The persisted shape uses
// numeric ids so this can never collide with a real variable id.
const NEW_VARIABLE_OPTION = '__fb_new_variable__';

// Variable-list editor used by:
//   - Save Variables (ModuleParameter.typeId === 17)
//   - XML-RPC Mappings (ModuleParameter.typeId === 22)
//
// Both modules store an ordered list of application variables as
// `{"id_1": varId, "id_2": varId, ...}` with keys numbered from 1.
// `valueType` is `variablelist` (we don't write a different valueType for
// XML-RPC because the legacy node-update SP stores everything as
// `valueType:value` and the engine inspects `value` only).
//
// Negative variable IDs are built-in CallContext variables; they come back
// from /api/flowbuilder/lookup/variablelist alongside the user-defined
// ones, so the picker handles them transparently.

interface Props {
  value: string;
  readOnly: boolean;
  applicationId: number;
  commit: (next: string) => void;
}

interface Row {
  // Stable client-only id for dnd-kit & React keys. The persisted
  // shape uses positional `id_N` keys, but we shouldn't change rendering
  // keys when the user reorders — that would defocus the active picker.
  rowKey: string;
  variableId: string;
}

let nextRowKey = 1;
function makeRowKey(): string {
  // Suffix bumps per call so two same-second rows still get unique keys.
  return `r${Date.now()}_${nextRowKey++}`;
}

function parseValue(raw: string): string[] {
  if (!raw) return [];
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return [];
    const ids: string[] = [];
    for (let i = 1; ; i++) {
      const v = (obj as Record<string, unknown>)[`id_${i}`];
      if (v == null || v === '') break;
      ids.push(String(v));
    }
    return ids;
  } catch {
    return [];
  }
}

function serializeValue(rows: Row[]): string {
  // Legacy semantics: if the list is empty we persist the JSON string
  // "null" (that's what the legacy editor does via `obj = null;
  // JSON.stringify(obj)`). The engine treats both "null" and "{}" as
  // "no variables to write", so keeping the legacy wire format avoids
  // any chance of unexpected behaviour on the telephony side.
  const withValues = rows.filter((r) => r.variableId);
  if (withValues.length === 0) return 'null';
  const obj: Record<string, string> = {};
  withValues.forEach((r, i) => {
    obj[`id_${i + 1}`] = r.variableId;
  });
  return JSON.stringify(obj);
}

export function VariableListEditor({ value, readOnly, applicationId, commit }: Props) {
  const initial = useMemo(() => {
    return parseValue(value).map<Row>((id) => ({ rowKey: makeRowKey(), variableId: id }));
  }, [value]);

  const [rows, setRows] = useState<Row[]>(initial);
  // Re-sync when the parent value changes from outside (e.g. another tab,
  // or an unrelated save replaces our optimistic state).
  useEffect(() => setRows(initial), [initial]);

  const { items: variables, loading: varLoading, error: varError } = useLookup(
    'variable',
    applicationId,
  );

  // Which row triggered "+ New variable…"; we open the modal centrally and
  // remember the originating rowKey so we can bind the new id back to it.
  const [createForRow, setCreateForRow] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const persist = useCallback(
    (next: Row[]) => {
      const serialized = serializeValue(next);
      if (serialized !== value) commit(serialized);
    },
    [commit, value],
  );

  const setRowVariable = (rowKey: string, varId: string) => {
    if (varId === NEW_VARIABLE_OPTION) {
      // Defer the row update until the modal closes successfully — the
      // select itself stays on its previous value via the controlled
      // `value=row.variableId` prop.
      setCreateForRow(rowKey);
      return;
    }
    setRows((prev) => {
      const next = prev.map((r) => (r.rowKey === rowKey ? { ...r, variableId: varId } : r));
      persist(next);
      return next;
    });
  };

  const removeRow = (rowKey: string) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.rowKey !== rowKey);
      persist(next);
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => {
      const next = [...prev, { rowKey: makeRowKey(), variableId: '' }];
      // No persist here: an empty row carries no value, so the serialised
      // output is unchanged. We only persist once the user picks a variable.
      return next;
    });
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setRows((prev) => {
      const oldIndex = prev.findIndex((r) => r.rowKey === active.id);
      const newIndex = prev.findIndex((r) => r.rowKey === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persist(next);
      return next;
    });
  };

  // Disable "Add" until the last row has a value picked — same behaviour as
  // the legacy `addNewVariableEnabled` computed observable.
  const lastRowEmpty = rows.length > 0 && !rows[rows.length - 1].variableId;

  // Variables already in use elsewhere in the list — we grey them out in
  // the picker so the user can't pick the same variable twice (legacy
  // `renderVariableListItem` disables matched options).
  const usedIds = useMemo(() => new Set(rows.map((r) => r.variableId).filter(Boolean)), [rows]);

  if (varError) {
    return <div className="fb-error-inline">Failed to load variables: {varError}</div>;
  }

  return (
    <div className="fb-varlist">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rows.map((r) => r.rowKey)} strategy={verticalListSortingStrategy}>
          {rows.map((row, idx) => (
            <SortableVariableRow
              key={row.rowKey}
              row={row}
              index={idx}
              variables={variables}
              varLoading={varLoading}
              usedIds={usedIds}
              readOnly={readOnly}
              onChange={(v) => setRowVariable(row.rowKey, v)}
              onRemove={() => removeRow(row.rowKey)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {rows.length === 0 && (
        <div className="fb-muted fb-varlist-empty">No variables added yet.</div>
      )}

      {!readOnly && (
        <button
          type="button"
          className="fb-btn fb-btn--ghost fb-varlist-add"
          onClick={addRow}
          disabled={lastRowEmpty}
          title={lastRowEmpty ? 'Pick a variable for the current row first' : 'Add another variable'}
        >
          + Add variable
        </button>
      )}

      <VariableCreateModal
        open={createForRow != null}
        applicationId={applicationId}
        onCancel={() => setCreateForRow(null)}
        onCreated={(id) => {
          const rowKey = createForRow;
          setCreateForRow(null);
          if (!rowKey) return;
          setRows((prev) => {
            const next = prev.map((r) =>
              r.rowKey === rowKey ? { ...r, variableId: id } : r,
            );
            persist(next);
            return next;
          });
        }}
      />
    </div>
  );
}

interface SortableRowProps {
  row: Row;
  index: number;
  variables: LookupItem[];
  varLoading: boolean;
  usedIds: Set<string>;
  readOnly: boolean;
  onChange: (varId: string) => void;
  onRemove: () => void;
}

function SortableVariableRow({
  row,
  index,
  variables,
  varLoading,
  usedIds,
  readOnly,
  onChange,
  onRemove,
}: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.rowKey,
    disabled: readOnly,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  // Variables come back grouped by category. We mirror the LookupEditor
  // optgroup layout so the picker stays familiar.
  const grouped = useMemo(() => groupByCategory(variables), [variables]);

  return (
    <div ref={setNodeRef} style={style} className="fb-varlist-row">
      {!readOnly && (
        <button
          type="button"
          className="fb-varlist-handle"
          aria-label="Reorder"
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </button>
      )}
      <span className="fb-varlist-idx">#{index + 1}</span>
      <select
        className="fb-input fb-varlist-select"
        value={row.variableId}
        disabled={readOnly || varLoading}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{varLoading ? 'Loading…' : 'Select Variable'}</option>
        {grouped.map((g, gi) =>
          g.category ? (
            <optgroup key={`${gi}-${g.category}`} label={g.category}>
              {g.items.map((it) => (
                <option
                  key={String(it.id)}
                  value={String(it.id)}
                  disabled={usedIds.has(String(it.id)) && String(it.id) !== row.variableId}
                >
                  {it.name.trim()}
                </option>
              ))}
            </optgroup>
          ) : (
            g.items.map((it) => (
              <option
                key={String(it.id)}
                value={String(it.id)}
                disabled={usedIds.has(String(it.id)) && String(it.id) !== row.variableId}
              >
                {it.name.trim()}
              </option>
            ))
          )
        )}
        {!readOnly && (
          <option value={NEW_VARIABLE_OPTION}>+ New variable…</option>
        )}
      </select>
      {!readOnly && (
        <button
          type="button"
          className="fb-btn fb-btn--ghost fb-btn--mini"
          onClick={onRemove}
          title="Remove this variable"
          aria-label="Remove"
        >
          ✕
        </button>
      )}
    </div>
  );
}

interface Group {
  category: string | null;
  items: LookupItem[];
}

function groupByCategory(items: LookupItem[]): Group[] {
  const result: Group[] = [];
  let currentCat: string | null | undefined = undefined;
  let currentItems: LookupItem[] = [];
  for (const it of items) {
    const cat = (it.category ?? null) as string | null;
    if (cat !== currentCat) {
      currentCat = cat;
      currentItems = [];
      result.push({ category: cat, items: currentItems });
    }
    currentItems.push(it);
  }
  return result;
}
